/* global console, document, window */
import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Text, Button, makeStyles } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import {
  incrementCellFontSize,
  incrementGlobalFontSize,
  updateStyle,
  updateTextPadding,
} from "../../../state/cellStylesSlice";
import { openStyleSheetDialog } from "../../../state/appStateSlice";

import {
  AlignCenter,
  Font,
  BackArrow,
  AlignTopLeft,
  AlignTopCenter,
  AlignTopRight,
  AlignCenterCenter,
  AlignCenterRight,
  AlignCenterLeft,
  AlignBottomLeft,
  AlignBottomCenter,
  AlignBottomRight,
  InvertTextColor,
  FontSizeIncrease,
  FontSizeDecrease,
  Margins,
  Bullets,
  Numbers,
  FullWidth,
  MediumShape,
  Medium5Shape,
  SmallShape,
  FlushLeft,
} from "../../../assets/icons";
import { onSelection } from "../../../helpers/selection";
import PropTypes from "prop-types";
import PaddingSubMenuItem from "./subMenuItems/PaddingSubMenuItem";
import { CaretDownIcon, CaretUpIcon, InfoIcon } from "@phosphor-icons/react";
import TrackedButton from "../../common/TrackedButton";

const useClasses = makeStyles({
  BackArrow: {
    marginRight: 4,
  },
  Header: {
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: 700,
    lineGeight: 11.5,
    textAlign: "left",
  },
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  PresetButton: {
    justifyContent: "left",
  },
  AccordionHeader: {
    "& > button": {
      minHeight: 0,
    },
  },
});

const buttonStyles = {
  selected: { backgroundColor: "#F5F5F5" },
  unselected: { backgroundColor: "transparent" },
};

const marginShortcuts = [
  {
    id: "full",
    icon: <FullWidth />,
    tooltip: "All 0px",
    to: {
      padding: 0,
    },
  },
  {
    id: "medium3",
    icon: <MediumShape />,
    tooltip: "All 3px",
    to: {
      padding: 3,
    },
  },
  {
    id: "medium5",
    icon: <Medium5Shape />,
    tooltip: "All 5px",
    to: {
      padding: 5,
    },
  },
  {
    id: "small",
    icon: <SmallShape />,
    tooltip: "All 10px",
    to: {
      padding: 10,
    },
  },
  {
    id: "flushLeft",
    icon: <FlushLeft />,
    tooltip: "Left 0px, other 3px",
    to: {
      paddingLeft: 0,
      paddingTop: 3,
      paddingRight: 3,
      paddingBottom: 3,
    },
  },
];

const TextStyleMenuItem = ({ isKeyboardSelected = false }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const [openPanelName, setOpenPanelName] = useState(null);
  const structure = useSelector((state) => state.gridd.present.structure);
  const selection = useSelector((state) => state.selection);
  const currentStyle = useSelector(
    (state) =>
      state.gridd.present.cellStyles[
        (
          selection.cells[0] ??
          (selection.rows.length > 0
            ? state.gridd.present.structure.cells[selection.rows[0]]
              ? state.gridd.present.structure.cells[selection.rows[0]][0]
              : null
            : selection.columns.length > 0 && state.gridd.present.structure.cells[0]
              ? state.gridd.present.structure.cells[0][selection.columns[0]]
              : null)
        )?.id
      ]
  );
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const dispatch = useDispatch();

  const isSingleCellSelected =
    selection.cells.length === 1 && selection.rows.length === 0 && selection.columns.length === 0 && !selection.table;

  function areWeInSubmenu() {
    return openPanelName === "font" || openPanelName === "align" || openPanelName === "bullets";
  }

  function getMenuButtons() {
    if (areWeInSubmenu()) {
      if (openPanelName === "font") {
        // TODO: why not using react ref and instead querying document directly? code smell
        return document.querySelectorAll('[id$="_preset_button"], #font_back_button');
      } else if (openPanelName === "align") {
        return document.querySelectorAll('[id$="_align_button"], #align_back_button');
      } else if (openPanelName === "bullets") {
        return document.querySelectorAll('[id$="_bullets_button"], #bullets_back_button');
      }
    }
    const mainMenuButtons = document.querySelectorAll('[id$="_panel_button"], [id$="_increment"], [id$="_decrement"]');
    return mainMenuButtons;
  }

  useEffect(() => {
    if (!isKeyboardSelected) return;

    const menuButtons = getMenuButtons();
    let currentFocusIndex = 0;

    if (menuButtons.length > 0) {
      menuButtons[0].focus();
    }

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
          console.log(
            "I'm focusing ",
            currentFocusIndex,
            menuButtons[currentFocusIndex],
            " in the UI you should see it focused now "
          );
          menuButtons[currentFocusIndex].focus();
          break;

        case "ArrowUp":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;

        case "Enter": {
          event.preventDefault();
          console.log(
            "I'm clicking ",
            currentFocusIndex,
            menuButtons[currentFocusIndex],
            " in the UI you should see the color change now "
          );

          // Get the current button
          const button = menuButtons[currentFocusIndex];

          // Special handling for color buttons
          if (button && button.id && button.id.startsWith("color_")) {
            const colorIndex = parseInt(button.id.split("_")[1], 10);
            console.log("Pressing Enter on color button:", button.id, "index:", colorIndex, "panel:", openPanelName);

            if (!isNaN(colorIndex)) {
              const colors = [{ name: "none", value: "none" }, ...currentStyleSheet.colors];
              const color = colors[colorIndex];

              console.log("Selected color:", color?.name, color?.value);
            }
          }

          // Default behavior for non-color buttons
          button.click();
          break;
        }

        case "F4":
          // Reset focus and keyboard navigation
          setOpenPanelName(null);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isKeyboardSelected, openPanelName, dispatch, selection, structure, currentStyleSheet]);

  return (
    <div style={{ border: isKeyboardSelected ? "2px solid #000000" : "none" }}>
      <div
        style={{
          display: openPanelName ? "none" : "grid",
          gridTemplateRows: "auto auto auto auto auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto auto auto auto",
            gap: "4px",
            justifyContent: "left",
            maxWidth: 148,
            marginLeft: 8,
          }}
        >
          <div style={{ alignContent: "center" }}>{currentStyle?.size}</div>
          <div style={{ display: "grid", gridTemplateRows: "auto auto" }}>
            <CommonTooltip content={t("menu.style.size.increment.cell")}>
              <TrackedButton
                id="font_size_increment"
                className={classes.PanelButton}
                style={{
                  justifyContent: "center",
                  minWidth: 0,
                  width: 16,
                  height: 16,
                }}
                appearance="subtle"
                icon={<CaretUpIcon size={12} />}
                onClick={() => onSelection(dispatch, incrementCellFontSize, { structure, selection }, { increment: 1 })}
              />
            </CommonTooltip>
            <CommonTooltip content={t("menu.style.size.decrement.cell")}>
              <TrackedButton
                id="font_size_decrement"
                className={classes.PanelButton}
                style={{
                  justifyContent: "center",
                  minWidth: 0,
                  width: 16,
                  height: 16,
                }}
                appearance="subtle"
                icon={<CaretDownIcon size={12} />}
                onClick={() =>
                  onSelection(dispatch, incrementCellFontSize, { structure, selection }, { increment: -1 })
                }
              />
            </CommonTooltip>
          </div>
          <CommonTooltip content={t("menu.style.size.increment.global")}>
            <TrackedButton
              id="font_size_increment"
              className={classes.PanelButton}
              style={{
                justifyContent: "center",
                minWidth: 0,
                width: 32,
              }}
              appearance="subtle"
              icon={<FontSizeIncrease />}
              onClick={() => dispatch(incrementGlobalFontSize(1))}
            />
          </CommonTooltip>
          <CommonTooltip content={t("menu.style.size.decrement.global")}>
            <TrackedButton
              id="font_size_decrement"
              className={classes.PanelButton}
              style={{
                justifyContent: "center",
                minWidth: 0,
                width: 32,
              }}
              appearance="subtle"
              icon={<FontSizeDecrease />}
              onClick={() => dispatch(incrementGlobalFontSize(-1))}
            />
          </CommonTooltip>
        </div>

        <Button
          id="font_panel_button"
          className={classes.PanelButton}
          appearance="subtle"
          icon={<Font />}
          onClick={() => {
            setOpenPanelName("font");
          }}
        >
          {t("menu.style.font")}
        </Button>
        <Button
          id="invert_panel_button"
          className={classes.PanelButton}
          appearance="subtle"
          icon={<InvertTextColor />}
          onClick={() => {
            const invertedPreset = currentStyleSheet.presets.find((c) => c.name === (currentStyle?.preset ?? "Body"));
            const color1 = currentStyleSheet.colors.find((c) => c.name === invertedPreset.fontColor);
            const color2 = currentStyleSheet.colors.find((c) => c.name === invertedPreset.fontColor2);
            const invertedColor =
              currentStyle?.color === color1?.value ||
              currentStyle?.colorName === color1?.name ||
              currentStyle?.color === null ||
              currentStyle?.color === undefined ||
              currentStyle?.colorName === null ||
              currentStyle?.colorName === undefined
                ? color2
                : color1;
            onSelection(
              dispatch,
              updateStyle,
              { selection, structure },
              {
                color: invertedColor.value,
                colorName: invertedColor.name,
                preset: currentStyle?.preset,
                isInverted: !currentStyle?.isInverted,
              }
            );
          }}
        >
          {t("menu.style.invert")}
        </Button>
        {isSingleCellSelected && (
          <Button
            id="bullets_panel_button"
            className={classes.PanelButton}
            appearance="subtle"
            icon={<Bullets />}
            onClick={() => {
              setOpenPanelName("bullets");
            }}
          >
            {t("menu.style.bulletsAndNumbers")}
          </Button>
        )}
        <Button
          id="align_panel_button"
          className={classes.PanelButton}
          appearance="subtle"
          icon={<AlignCenter />}
          onClick={() => {
            setOpenPanelName("align");
          }}
        >
          {t("menu.style.align")}
        </Button>
        <Button
          id="padding_panel_button"
          className={classes.PanelButton}
          appearance="subtle"
          icon={<Margins />}
          onClick={() => {
            setOpenPanelName("padding");
          }}
        >
          {t("menu.style.padding")}
        </Button>

        <div style={{ display: "grid", gridTemplateRows: "auto auto", gap: 4 }}>
          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.margin.shortcuts")}</Text>

          <div
            style={{
              paddingLeft: 8,
              display: "grid",
              gridTemplateColumns: "repeat(" + marginShortcuts.length + ", 1fr)",
              gap: 2,
            }}
          >
            {marginShortcuts.map((option) => (
              <CommonTooltip key={option.id} content={option.tooltip}>
                <Button
                  icon={option.icon}
                  size="small"
                  appearance="subtle"
                  onClick={() => {
                    onSelection(
                      dispatch,
                      updateTextPadding,
                      { structure, selection },
                      {
                        padding: null,
                        paddingLeft: null,
                        paddingTop: null,
                        paddingRight: null,
                        paddingBottom: null,
                        ...option.to,
                      }
                    );
                  }}
                />
              </CommonTooltip>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: openPanelName === "font" ? "grid" : "none", gridTemplateRows: "auto 1fr auto" }}>
        <div>
          <Button
            id="font_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.style.font")}</Text>
        </div>
        <div
          style={{
            maxHeight: "200px",
            overflowY: "scroll",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateRows: currentStyleSheet.presets?.reduce((t) => t + "auto ", ""),
            }}
          >
            {currentStyleSheet.presets?.map((preset) => (
              <React.Fragment key={preset.name}>
                <TrackedButton
                  id={`${preset.name.toLowerCase()}_preset_button`}
                  appearance="subtle"
                  style={currentStyle?.preset === preset.name ? buttonStyles.selected : buttonStyles.unselected}
                  className={classes.PresetButton}
                  onClick={() => {
                    const currentColorType = currentStyle?.isInverted ? preset.fontColor2 : preset.fontColor;
                    onSelection(
                      dispatch,
                      updateStyle,
                      { structure, selection },
                      {
                        weight: preset.fontWeight,
                        style: preset.fontStyle,
                        color: currentStyleSheet.colors.find((c) => c.name === currentColorType)?.value ?? "black",
                        colorName: currentStyleSheet.colors.find((c) => c.name === currentColorType)?.name ?? "text",
                        fontUnderlined: preset.fontUnderlined,
                        fontFamily: preset.fontFamily,
                        preset: preset.name,
                        presetSize: preset.fontSize,
                      }
                    );
                  }}
                >
                  <Text
                    style={{
                      fontFamily: preset.fontFamily ?? "Arial",
                      fontWeight: preset.fontWeight,
                      fontStyle: preset.fontStyle,
                      fontSize: preset.fontSize ?? "12pt",
                      textDecoration: preset.fontUnderlined ? "underline" : undefined,
                      color: currentStyleSheet.colors.find((c) => c.name === preset.fontColor)?.value ?? "black",
                    }}
                  >
                    {t("menu.style.presets." + preset.name)}
                  </Text>
                </TrackedButton>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 8, textAlign: "left", marginLeft: 12 }}>
          <a
            href="#"
            style={{
              color: "#0065FD",
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onClick={(e) => {
              e.preventDefault();
              dispatch(openStyleSheetDialog());
              // TODO: should have been a state in the appStateSlice, like "initialTab"
              window.setTimeout(() => {
                const tab = window.document.getElementById("presets");
                if (tab) tab.click();
              }, 100);
            }}
          >
            {t("menu.stylesheet.editTextStyles")}
          </a>
        </div>
      </div>
      <div style={{ display: openPanelName === "align" ? "grid" : "none", gridTemplateRows: "auto, 1fr" }}>
        <div>
          <Button
            id="align_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.style.align")}</Text>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "36px 36px 36px",
            gridTemplateRows: "auto auto auto",
          }}
        >
          <TrackedButton
            id="text_menu_home_top_left_align_button"
            icon={<AlignTopLeft style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "left" && currentStyle?.verticalAlign === "top"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(dispatch, updateStyle, { structure, selection }, { textAlign: "left", verticalAlign: "top" });
            }}
          />
          <TrackedButton
            id="text_menu_home_top_center_align_button"
            icon={<AlignTopCenter style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "center" && currentStyle?.verticalAlign === "top"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "center", verticalAlign: "top" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_top_right_align_button"
            icon={<AlignTopRight style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "right" && currentStyle?.verticalAlign === "top"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "right", verticalAlign: "top" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_center_left_align_button"
            icon={<AlignCenterLeft style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "left" && currentStyle?.verticalAlign === "middle"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "left", verticalAlign: "middle" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_center_center_align_button"
            icon={<AlignCenterCenter style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "center" && currentStyle?.verticalAlign === "middle"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "center", verticalAlign: "middle" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_center_right_align_button"
            icon={<AlignCenterRight style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "right" && currentStyle?.verticalAlign === "middle"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "right", verticalAlign: "middle" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_bottom_left_align_button"
            icon={<AlignBottomLeft style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "left" && currentStyle?.verticalAlign === "bottom"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "left", verticalAlign: "bottom" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_bottom_center_align_button"
            icon={<AlignBottomCenter style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "center" && currentStyle?.verticalAlign === "bottom"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "center", verticalAlign: "bottom" }
              );
            }}
          />
          <TrackedButton
            id="text_menu_home_bottom_right_align_button"
            icon={<AlignBottomRight style={{ width: 24, height: 24 }} />}
            appearance="subtle"
            style={
              currentStyle?.textAlign === "right" && currentStyle?.verticalAlign === "bottom"
                ? buttonStyles.selected
                : buttonStyles.unselected
            }
            className={classes.PanelButton}
            onClick={() => {
              onSelection(
                dispatch,
                updateStyle,
                { structure, selection },
                { textAlign: "right", verticalAlign: "bottom" }
              );
            }}
          />
        </div>
      </div>
      <div style={{ display: openPanelName === "padding" ? "grid" : "none", gridTemplateRows: "auto, 1fr" }}>
        <div>
          <Button
            id="padding_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.style.padding")}</Text>
          <CommonTooltip content={t("menu.text.padding.info")} showDelay={500}>
            <InfoIcon size={16} />
          </CommonTooltip>
        </div>
        <PaddingSubMenuItem
          currentStyleSheet={currentStyleSheet}
          currentStyle={currentStyle}
          propertyName="padding"
          objectName="text"
        />
      </div>
      <div
        style={{
          display: openPanelName === "bullets" && isSingleCellSelected ? "grid" : "none",
          gridTemplateRows: "auto 1fr",
        }}
      >
        <div>
          <Button
            id="bullets_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.style.bulletsAndNumbers")}</Text>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <CommonTooltip content={t("menu.style.bullets.tooltip")}>
            <TrackedButton
              id="bullets_style_panel_button"
              className={classes.PanelButton}
              appearance="subtle"
              icon={<Bullets />}
              onClick={() => {
                onSelection(dispatch, updateStyle, { selection, structure }, { bullets: "bullets" });
              }}
            >
              {t("menu.style.bullets")}
            </TrackedButton>
          </CommonTooltip>
          <CommonTooltip content={t("menu.style.numbers.tooltip")}>
            <TrackedButton
              id="numbers_style_panel_button"
              className={classes.PanelButton}
              appearance="subtle"
              icon={<Numbers />}
              onClick={() => {
                onSelection(dispatch, updateStyle, { selection, structure }, { bullets: "numbers" });
              }}
            >
              {t("menu.style.numbers")}
            </TrackedButton>
          </CommonTooltip>
        </div>
      </div>
    </div>
  );
};

TextStyleMenuItem.propTypes = {
  isKeyboardSelected: PropTypes.bool,
};

export default TextStyleMenuItem;
